/**
 * LỚP HIỆU ỨNG — nơi "cảm giác đánh" được tạo ra.
 *
 * Kiến trúc:
 *   · Mô phỏng (sim) chỉ phát ra DỮ LIỆU (`EffectBurst`: vị trí, bán kính, màu, hướng, id).
 *   · Lớp này biến dữ liệu đó thành hình: vòng xung kích, vệt chém, tia lửa, bụi,
 *     vết nứt đất, loé sáng, cùng HẠT (particle) có vận tốc/ trọng lực riêng.
 *
 * Ba thứ tạo cảm giác "hoành tráng", đều nằm ở đây:
 *   1. NHIỀU LỚP cho mỗi cú đánh (tia va chạm → vệt chém → tia lửa → loé sáng).
 *   2. HẠT BAY có vận tốc, trọng lực, mờ dần — thay vì 1 ảnh phóng to.
 *   3. RUNG MÀN HÌNH + ZOOM PUNCH + LOÉ TOÀN MÀN HÌNH theo độ nặng của đòn.
 *
 * Toàn bộ dùng POOL cố định: không cấp phát đối tượng mỗi frame ⇒ FPS ổn định.
 */
import Phaser from 'phaser';
import type { EffectBurst, FloatText } from '../sim/types';
import type { World } from '../sim/world';
import {
  T,
  aiCell,
  ensureVfxTextures,
  fallbackTexture,
  loadUltFxSheet,
  loadVfxSheets,
  type VfxSheet,
} from './vfx-textures';

/** Ô ảnh AI cho từng loại hiệu ứng (chỉ số ô trong lưới 4×4, 0..15). */
/**
 * Ô ẢNH AI CHO TỪNG LOẠI HIỆU ỨNG.
 * `ult:<art>` = lấy từ lưới ultimate (`ULT_ART`), không phải lưới hit/ skill.
 */
const AI_CELL: Record<string, [sheet: 'hit' | 'skill', index: number]> = {
  impact: ['hit', 0],
  hit: ['hit', 0],
  slash: ['hit', 1],
  dust: ['hit', 2],
  spark: ['hit', 3],
  die: ['hit', 8],
  ring: ['skill', 7],
  shock: ['hit', 5],
  aoe: ['skill', 7],
  crack: ['skill', 11],
  flash: ['hit', 7],
  streak: ['hit', 12],
  aura: ['skill', 4],
  swirl: ['skill', 5],
  skill: ['skill', 0],
  spawn: ['skill', 10],
};

interface Particle {
  img: Phaser.GameObjects.Image;
  active: boolean;
  x: number;
  y: number;
  vx: number;
  vy: number;
  grav: number;
  drag: number;
  life: number;
  ttl: number;
  size: number;
  rot: number;
  vr: number;
  color: number;
  streak: boolean;
}

interface BurstView {
  img: Phaser.GameObjects.Image;
  /** id vụ đã gán vào ảnh này (để không vẽ lại lung tung) */
  burstId: number;
}

const MAX_PARTICLES = 340;
const MAX_TEXT = 180;
const MAX_BURSTS = 420;

export class FxLayer {
  private texts: Phaser.GameObjects.Text[] = [];
  private burstImgs: BurstView[] = [];
  private particles: Particle[] = [];
  private flashRect: Phaser.GameObjects.Rectangle;
  private flashAlpha = 0;
  private flashColor = 0xffffff;
  /** dòng chữ "TUYỆT KỸ" giữa màn hình */
  private calloutText: Phaser.GameObjects.Text;
  private calloutUntil = 0;
  private lastBurstId = 0;
  private sheets: Record<'hit' | 'skill', VfxSheet | null> = { hit: null, skill: null };
  /**
   * LƯỚI ẢNH RIÊNG CHO ULTIMATE (do `tools/gen-image.mjs` sinh, xem `ULT_ART`).
   * ⚠️ Tách khỏi `sheets` (hit/ skill) vì đây là lưới 3×3 nội dung KHÁC HẲN (máy bay/ bom xăng),
   * không phải hiệu ứng va chạm — nhét chung sẽ làm `AI_CELL` phải đánh số lại toàn bộ.
   */
  private ultSheets = new Map<string, VfxSheet | null>();
  private rngState = 987654321;
  /** rung + zoom gộp lại trong 1 frame để không chồng 2 hiệu ứng camera */
  private pendingShake = 0;
  private zoomUntil = 0;

  constructor(private scene: Phaser.Scene) {
    ensureVfxTextures(scene);
    this.flashRect = scene.add
      .rectangle(0, 0, 64, 64, 0xffffff, 0)
      .setOrigin(0, 0)
      .setScrollFactor(0)
      .setDepth(8800)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setVisible(false);
    /*
     * CHỮ TUYỆT KỸ: nằm ở lớp giao diện (không theo camera) nên luôn đọc được,
     * có viền dày để nổi trên nền sân lộn xộn. Chỉ hiện với chiêu "nặng".
     */
    this.calloutText = scene.add
      .text(500, 190, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '34px',
        color: '#fde68a',
        fontStyle: 'bold',
        stroke: '#7c2d12',
        strokeThickness: 7,
        align: 'center',
      })
      .setOrigin(0.5, 0.5)
      .setScrollFactor(0)
      .setDepth(8900)
      .setVisible(false);
    /*
     * ⚠️ BẪY ĐÃ SẬP: `emit()` chỉ lấy hạt từ pool ĐÃ CÓ. Nếu pool còn rỗng thì hàm
     * lặp không thấy phần tử nào ⇒ KHÔNG BAO GIỜ có hạt, mà cũng không báo lỗi.
     * Vì vậy phải dựng sẵn cả pool ngay từ đầu.
     */
    for (let i = 0; i < MAX_PARTICLES; i++) this.particleAt(i);
    void this.loadSheets();
  }

  private async loadSheets(): Promise<void> {
    this.sheets = await loadVfxSheets(this.scene);
    /*
     * LƯỚI ẢNH RIÊNG CHO ULTIMATE (máy bay · bom xăng). Chưa có ảnh ⇒ để `null`, `texFor` tự lùi
     * về texture vẽ bằng code (game không bao giờ vỡ hình).
     */
    for (const art of ['maybay', 'xebom']) {
      this.ultSheets.set(art, await loadUltFxSheet(this.scene, art));
    }
    const n = (this.sheets.hit ? this.sheets.hit.cells.filter(Boolean).length : 0) + (this.sheets.skill ? this.sheets.skill.cells.filter(Boolean).length : 0);
    console.log(`[vfx] ảnh AI: ${n} ô hiệu ứng (hit ${this.sheets.hit ? 'có' : 'không'} · skill ${this.sheets.skill ? 'có' : 'không'})`);
  }

  /** true nếu ít nhất 1 trong 2 ảnh VFX AI đã nạp được — công cụ kiểm chứng đọc. */
  get aiCellCount(): number {
    return (this.sheets.hit ? this.sheets.hit.cells.filter(Boolean).length : 0) + (this.sheets.skill ? this.sheets.skill.cells.filter(Boolean).length : 0);
  }

  /** Số hạt đang bay — công cụ kiểm chứng dùng để chứng minh hiệu ứng có chạy thật. */
  get particleCount(): number {
    let n = 0;
    for (const p of this.particles) if (p.active) n++;
    return n;
  }

  /** Số vụ hiệu ứng đang hiển thị. */
  get burstViewCount(): number {
    let n = 0;
    for (const b of this.burstImgs) if (b.img.visible) n++;
    return n;
  }

  // ---------------------------------------------------------------- RNG nội bộ

  private rnd(): number {
    this.rngState = (this.rngState * 1664525 + 1013904223) % 4294967296;
    return this.rngState / 4294967296;
  }

  private range(a: number, b: number): number {
    return a + this.rnd() * (b - a);
  }

  // ---------------------------------------------------------------- pool

  private textAt(i: number): Phaser.GameObjects.Text {
    while (this.texts.length <= i) {
      this.texts.push(
        this.scene.add
          .text(0, 0, '', { fontFamily: 'system-ui, sans-serif', fontSize: '16px', color: '#fff', fontStyle: 'bold' })
          .setOrigin(0.5, 1)
          .setStroke('#1c1917', 4)
          .setVisible(false),
      );
    }
    return this.texts[i];
  }

  private burstAt(i: number): BurstView {
    while (this.burstImgs.length <= i) {
      this.burstImgs.push({
        img: this.scene.add.image(0, 0, T.glow).setBlendMode(Phaser.BlendModes.ADD).setVisible(false),
        burstId: -1,
      });
    }
    return this.burstImgs[i];
  }

  private particleAt(i: number): Particle {
    while (this.particles.length <= i) {
      this.particles.push({
        img: this.scene.add.image(0, 0, T.spark).setBlendMode(Phaser.BlendModes.ADD).setVisible(false),
        active: false, x: 0, y: 0, vx: 0, vy: 0, grav: 0, drag: 0,
        life: 0, ttl: 1, size: 8, rot: 0, vr: 0, color: 0xffffff, streak: false,
      });
    }
    return this.particles[i];
  }

  // ---------------------------------------------------------------- phát hạt

  private emit(x: number, y: number, count: number, opt: {
    color: number; speed: number; spreadA?: number; baseA?: number; ttl?: number;
    size?: number; grav?: number; drag?: number; streak?: boolean; rise?: number;
  }): void {
    let made = 0;
    /*
     * Lặp tới MAX_PARTICLES và dùng `particleAt()` để TỰ DỰNG pool nếu còn thiếu.
     * ⚠️ BẪY ĐÃ SẬP: `clear()` (gọi mỗi lần vào màn mới) từng xoá sạch mảng hạt ⇒
     * `emit()` lặp trên mảng RỖNG ⇒ không bao giờ có hạt, mà cũng không báo lỗi gì.
     */
    for (let i = 0; i < MAX_PARTICLES && made < count; i++) {
      const p = this.particleAt(i);
      if (p.active) continue;
      made++;
      const a = (opt.baseA ?? 0) + this.range(-(opt.spreadA ?? Math.PI), opt.spreadA ?? Math.PI);
      const sp = opt.speed * this.range(0.45, 1.15);
      p.active = true;
      p.x = x + this.range(-6, 6);
      p.y = y + this.range(-6, 6);
      p.vx = Math.cos(a) * sp;
      p.vy = Math.sin(a) * sp - (opt.rise ?? 0);
      p.grav = opt.grav ?? 0;
      p.drag = opt.drag ?? 2.4;
      p.ttl = (opt.ttl ?? 0.42) * this.range(0.7, 1.3);
      p.life = p.ttl;
      p.size = (opt.size ?? 9) * this.range(0.7, 1.35);
      p.rot = a;
      p.vr = this.range(-6, 6);
      p.color = opt.color;
      p.streak = opt.streak ?? false;
      p.img.setTexture(p.streak ? T.spark : T.glow);
      p.img.setVisible(true);
      break;
    }
  }

  /**
   * Gieo hạt theo một VỤ MỚI. Đây là chỗ biến dữ liệu sim thành "pháo hoa".
   * Chỉ gọi cho vụ có id > lastBurstId ⇒ không bắn lại hạt cho vụ cũ.
   */
  private seedParticles(b: EffectBurst): void {
    switch (b.kind) {
      case 'impact':
      case 'hit':
        this.emit(b.x, b.y, 7, { color: b.color, baseA: b.angle ?? 0, spreadA: 1.1, speed: 150 * (b.scale ?? 1), ttl: 0.3, size: 8, drag: 4 });
        this.emit(b.x, b.y, 3, { color: 0xffffff, baseA: b.angle ?? 0, spreadA: 1.4, speed: 90, ttl: 0.18, size: 6, drag: 6, streak: true });
        break;
      case 'spark':
        this.emit(b.x, b.y, 4, { color: b.color, baseA: b.angle ?? 0, spreadA: 0.5, speed: 240, ttl: 0.3, size: 7, drag: 3.4, streak: true });
        break;
      case 'die':
        this.emit(b.x, b.y, 12, { color: b.color, spreadA: Math.PI, speed: 190, ttl: 0.7, size: 10, grav: 420, drag: 1.2 });
        this.emit(b.x, b.y - 6, 8, { color: 0xffffff, spreadA: Math.PI, speed: 120, ttl: 0.4, size: 8, drag: 3, streak: true });
        break;
      case 'dust':
        this.emit(b.x, b.y, 5, { color: b.color, spreadA: Math.PI, speed: 60, ttl: 0.7, size: 18, drag: 2.2, rise: 26 });
        break;
      case 'crack':
        this.emit(b.x, b.y, 7, { color: 0xcbb79a, spreadA: Math.PI, speed: 170, ttl: 0.55, size: 7, grav: 520, drag: 1.5 });
        break;
      case 'flash':
        this.emit(b.x, b.y, 4, { color: b.color, spreadA: Math.PI, speed: 130, ttl: 0.22, size: 7, drag: 5, streak: true });
        break;
      case 'ring':
      case 'shock':
      case 'aoe':
        this.emit(b.x, b.y, 6, { color: b.color, spreadA: Math.PI, speed: Math.max(90, b.radius * 1.5), ttl: 0.45, size: 8, drag: 2.6 });
        break;
      case 'skill':
      case 'aura':
      case 'swirl':
        this.emit(b.x, b.y, 6, { color: b.color, spreadA: Math.PI, speed: 90, ttl: 0.55, size: 9, drag: 2, rise: 60 });
        break;
      case 'spawn':
        this.emit(b.x, b.y, 5, { color: b.color, spreadA: Math.PI, speed: 80, ttl: 0.5, size: 9, drag: 2, rise: 40 });
        break;
      case 'streak':
        this.emit(b.x, b.y, 3, { color: b.color, baseA: b.angle ?? 0, spreadA: 0.2, speed: 260, ttl: 0.24, size: 7, drag: 5, streak: true });
        break;
      default:
        break;
    }
  }

  // ---------------------------------------------------------------- vẽ

  render(floats: FloatText[], bursts: EffectBurst[], dt = 1 / 60, world?: World): void {
    this.renderFloats(floats);
    this.renderBursts(bursts, dt);
    /*
     * VÙNG CHÁY + LỬA TRÊN NGƯỜI QUÁI ĐANG CHÁY.
     *
     * Người dùng: *"có hiệu ứng cháy phạm vi ảnh hưởng, enemy đang bị thiêu đốt cũng sẽ có hiệu ứng
     * cháy trên người"*.
     * ⚠️ Vẽ theo DỮ LIỆU CỦA SIM (`world.fireZonesActive` + `buffs` có `burn`), KHÔNG tự đếm thời
     * gian ở client — nếu không thì hình lửa và sát thương thật sẽ lệch nhau (lửa tắt mà quái vẫn
     * mất máu, hoặc ngược lại).
     */
    if (world) this.renderFire(world, dt);
    this.updateParticles(dt);
    this.updateFlash(dt);
    this.updateCallout();
    this.consumeShake();
    void this.zoomUntil;
  }

  /**
   * VÙNG CHÁY (vòng lửa dưới đất) + LỬA TRÊN NGƯỜI từng quái đang bị thiêu đốt.
   * Dùng texture vẽ bằng code (`fallbackTexture`) nên không cần ảnh mới.
   */
  private fireZoneImgs: Phaser.GameObjects.Image[] = [];
  private burnImgs: Phaser.GameObjects.Image[] = [];

  private renderFire(world: World, dt: number): void {
    void dt;
    // ---- 1) VÒNG LỬA PHẠM VI ẢNH HƯỞNG
    const zones = world.fireZonesActive;
    while (this.fireZoneImgs.length < zones.length) {
      this.fireZoneImgs.push(
        this.scene.add
          .image(0, 0, fallbackTexture('aoe'))
          .setOrigin(0.5, 0.5)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setVisible(false),
      );
    }
    zones.forEach((z, i) => {
      const img = this.fireZoneImgs[i];
      const left = Math.max(0, z.until - world.time);
      // vòng lửa co dần + mờ dần khi sắp tắt ⇒ người chơi biết vùng sắp hết
      const k = Math.min(1, left / 1.2);
      img
        .setPosition(z.x, z.y)
        .setDisplaySize(z.radius * 2.1, z.radius * 1.05) // dẹt theo phối cảnh sân
        .setTint(0xf97316)
        .setAlpha(0.34 * k + 0.12)
        .setDepth(z.y * 10 - 3) // NẰM DƯỚI nhân vật (như vũng lửa trên đất)
        .setVisible(true);
    });
    for (let i = zones.length; i < this.fireZoneImgs.length; i++) this.fireZoneImgs[i].setVisible(false);

    // ---- 2) LỬA TRÊN NGƯỜI quái đang bị thiêu đốt
    const burning = world.units.filter((u) => u.alive && u.team === 'enemy' && u.buffs.some((b) => b.id === 'burn'));
    while (this.burnImgs.length < burning.length) {
      this.burnImgs.push(
        this.scene.add
          .image(0, 0, fallbackTexture('spark'))
          .setOrigin(0.5, 1)
          .setBlendMode(Phaser.BlendModes.ADD)
          .setVisible(false),
      );
    }
    burning.forEach((u, i) => {
      const img = this.burnImgs[i];
      // nhấp nháy nhẹ để lửa "sống", dùng thời gian sim nên tất định
      const flick = 0.75 + 0.25 * Math.sin(world.time * 14 + u.id);
      img
        .setPosition(u.x, u.y + 4)
        .setDisplaySize(u.radius * 2.2 * flick, u.radius * 3.4 * flick)
        .setTint(0xfbbf24)
        .setAlpha(0.62)
        .setDepth(u.y * 10 + 40) // TRÊN nhân vật để thấy lửa bốc quanh người
        .setVisible(true);
    });
    for (let i = burning.length; i < this.burnImgs.length; i++) this.burnImgs[i].setVisible(false);
  }

  private renderFloats(floats: FloatText[]): void {
    for (let i = 0; i < floats.length; i++) {
      const f = floats[i];
      const t = this.textAt(i);
      const k = Math.min(1, f.ttl / 0.9);
      t.setText(f.text);
      // số sát thương BAY LÊN và phóng to dần ⇒ đọc được cả khi màn hình đầy quái
      const rise = (1 - k) * 14;
      t.setPosition(f.x, f.y - rise);
      const pop = f.kind === 'crit' ? 1.55 : f.kind === 'dodge' ? 1.1 : 1;
      t.setAlpha(Math.min(1, k * 1.6));
      t.setScale((0.8 + (1 - k) * 0.35) * pop);
      t.setColor('#' + (f.color & 0xffffff).toString(16).padStart(6, '0'));
      t.setStroke(f.kind === 'crit' ? '#7c2d12' : '#1c1917', f.kind === 'crit' ? 5 : 4);
      t.setDepth(5000 + f.y);
      t.setVisible(true);
    }
    for (let i = floats.length; i < this.texts.length; i++) this.texts[i].setVisible(false);
  }

  private texFor(b: EffectBurst, variant: number): { tex: string; frame?: string; tint: number; boost: number } {
    /*
     * MÁY BAY + BOM XĂNG dùng lưới ảnh RIÊNG của ultimate (Gemini sinh).
     * Ô 1 = máy bay/ bom chính; xoay vòng 2 ô để có biến thể nhỏ/ lớn.
     * Không nhuộm màu: art đã có màu sẵn (nhuộm sẽ ra màu bùn — xem `tintFor`).
     */
    if (b.kind === 'plane' || b.kind === 'firebomb') {
      const art = b.kind === 'plane' ? 'maybay' : 'xebom';
      const cell = aiCell(this.ultSheets.get(art) ?? null, variant % 2);
      if (cell) return { tex: cell.tex, frame: cell.frame, tint: 0xffffff, boost: 1.28 };
    }
    const map = AI_CELL[b.kind];
    if (map) {
      const cell = aiCell(this.sheets[map[0]], map[1]);
      // ảnh AI đã có màu sẵn ⇒ chỉ nhuộm khi ô đó là hình trắng
      // `boost`: ô ảnh AI là lưới 256px và hiệu ứng chỉ chiếm ~80% ô ⇒ bù lại cho đủ to
      if (cell) return { tex: cell.tex, frame: cell.frame, tint: this.tintFor(b, map[0], map[1], variant), boost: 1.28 };
    }
    return { tex: fallbackTexture(b.kind), tint: b.color, boost: 1 };
  }

  /** Ô ảnh AI nào vốn đã nhiều màu thì KHÔNG nhuộm (nhuộm sẽ ra màu bùn). */
  private tintFor(b: EffectBurst, sheet: 'hit' | 'skill', index: number, _variant: number): number {
    void sheet;
    void index;
    void _variant;
    switch (b.kind) {
      /*
       * Ô ảnh AI là hình TRẮNG/ BẠC thì nhuộm theo màu của chiêu — nhờ vậy
       * "Giáng Long" ra xanh, "Chợ Vỡ" ra vàng, không chiêu nào giống chiêu nào.
       * Ô nhiều màu sẵn (lửa, tia chớp, khói) thì để nguyên.
       */
      case 'spark':
      case 'crack':
      case 'flash':
      case 'streak':
      case 'dust':
      case 'ring':
      case 'shock':
      case 'aoe':
      case 'impact':
      case 'slash':
      case 'hit':
        return b.color;
      default:
        return 0xffffff;
    }
  }

  private renderBursts(bursts: EffectBurst[], dt: number): void {
    for (let i = 0; i < bursts.length; i++) {
      const b = bursts[i];
      const view = this.burstAt(i);
      // VỤ MỚI ⇒ gieo hạt + loé màn hình + xếp hàng rung
      if (b.id > this.lastBurstId) {
        this.seedParticles(b);
        // loé toàn màn hình: đủ để "nảy" mắt, không đủ để che sân
        if (b.kind === 'flash') this.addFlash(b.color, Math.min(0.22, b.radius / 420), b.scale ?? 1);
        if (b.kind === 'ring' || b.kind === 'shock' || b.kind === 'aoe' || b.kind === 'die') {
          this.pendingShake = Math.max(this.pendingShake, Math.min(1.6, b.radius / 180));
        }
      }
      const k = Math.max(0, b.ttl / Math.max(0.0001, b.maxTtl));
      const grow = 1.5 - k * 0.5; // hiệu ứng "bung ra"
      const { tex, frame, tint, boost } = this.texFor(b, i % 3);
      const img = view.img;
      if (view.burstId !== b.id) {
        view.burstId = b.id;
        img.setTexture(tex, frame);
      }
      img.setPosition(b.x, b.y);
      img.setTint(tint);
      const spin = b.spin ?? 0;
      const s = (b.scale ?? 1) * boost;

      switch (b.kind) {
        /*
         * MÁY BAY + BOM XĂNG — CỠ RIÊNG, KHÔNG dùng công thức chung.
         * ⚠️ LỖI ĐÃ SỬA (đo trên ảnh chụp thật): để chung nhánh `aoe` thì ô ảnh 3×3 (341px) × boost
         * 1.28 bị phóng quá khổ ⇒ máy bay TO HƠN CẢ SÂN và tràn ra ngoài khung. Máy bay phải là
         * "vật thể bay ngang trời", cỡ ~1/4 chiều rộng sân; bom xăng cỡ bàn tay.
         */
        case 'plane':
          img.setDisplaySize(b.radius * 4.6, b.radius * 1.9);
          img.setRotation(0);
          img.setAlpha(Math.min(1, k * 1.6));
          break;
        case 'firebomb':
          img.setDisplaySize(b.radius * 1.5 * grow, b.radius * 1.5 * grow);
          img.setRotation(b.spin ?? 0);
          img.setAlpha(Math.min(1, k * 1.8));
          break;
        case 'aoe':
        case 'shock':
          // ⚠️ Đã thử để alpha 0,9 + cỡ 2,7× bán kính: sóng trắng XOÁ TRẮNG cả sân,
          // nhân vật biến mất ⇒ "hoành tráng" mà không chơi được. Hạ xuống mức này.
          img.setDisplaySize(b.radius * 2.3 * grow * s, b.radius * 0.85 * grow * s);
          img.setRotation(0);
          img.setAlpha(k * 0.6);
          break;
        case 'ring':
          img.setDisplaySize(b.radius * 2.2 * grow * s, b.radius * 2.2 * grow * s);
          img.setRotation(0);
          img.setAlpha(k * 0.5);
          break;
        case 'beam':
          img.setDisplaySize(b.radius * 3.0 * grow, b.radius * 1.5);
          img.setRotation(b.angle ?? 0);
          img.setAlpha(k * 0.9);
          break;
        case 'slash':
          img.setDisplaySize((b.len ?? b.radius * 2.6) * (0.7 + (1 - k) * 0.5), (b.len ?? b.radius * 2.6) * 0.55);
          img.setRotation((b.angle ?? 0) + (1 - k) * 1.1);
          img.setAlpha(Math.pow(k, 0.6) * 0.95);
          break;
        case 'impact':
        case 'hit':
          img.setDisplaySize(b.radius * 2.2 * (1.35 - k * 0.35), b.radius * 2.2 * (1.35 - k * 0.35));
          img.setRotation(b.angle ?? 0);
          img.setAlpha(Math.pow(k, 0.5) * 0.9);
          break;
        case 'spark':
        case 'streak': {
          const len = (b.len ?? b.radius * 3) * (0.6 + (1 - k) * 0.8);
          img.setDisplaySize(len, Math.max(3, b.radius * 1.1));
          img.setRotation(b.angle ?? 0);
          // tia bay RA theo hướng của nó
          img.setPosition(b.x + Math.cos(b.angle ?? 0) * len * (1 - k) * 0.5, b.y + Math.sin(b.angle ?? 0) * len * (1 - k) * 0.5);
          img.setAlpha(k);
          break;
        }
        case 'dust':
          img.setDisplaySize(b.radius * 2.6 * grow * s, b.radius * 2.1 * grow * s);
          img.setRotation((b.angle ?? 0) + (1 - k) * 0.4);
          img.setPosition(b.x, b.y - (1 - k) * 12);
          img.setAlpha(Math.pow(k, 0.8) * 0.7);
          break;
        case 'crack':
          img.setDisplaySize(b.radius * 2.4 * s, b.radius * 1.5 * s);
          img.setRotation(0);
          img.setAlpha(Math.pow(k, 0.7) * 0.95);
          break;
        case 'flash':
          // loé TẠI CHỖ: nhỏ hơn loé toàn màn hình, nếu không sẽ thành "đèn pha" che sân
          img.setDisplaySize(b.radius * 2.3 * (0.7 + (1 - k) * 0.6), b.radius * 2.3 * (0.7 + (1 - k) * 0.6));
          img.setRotation(0);
          img.setAlpha(Math.pow(k, 0.35) * 0.75);
          break;
        case 'swirl':
        case 'aura':
          img.setDisplaySize(b.radius * 2.4 * grow * s, b.radius * 2.4 * grow * s);
          img.setRotation((1 - k) * 3.4 * (spin || 1));
          img.setAlpha(k * 0.8);
          break;
        case 'die':
          img.setDisplaySize(b.radius * 2.6 * grow * s, b.radius * 2.6 * grow * s);
          img.setRotation((1 - k) * 1.6);
          img.setAlpha(Math.pow(k, 0.6));
          break;
        default:
          img.setDisplaySize(b.radius * 2.6 * grow * s, b.radius * 2.6 * grow * s);
          img.setRotation((1 - k) * 1.2 * (spin || 1));
          img.setAlpha(k * 0.85);
      }
      img.setDepth(4000 + b.y);
      img.setVisible(true);
    }
    for (let i = bursts.length; i < this.burstImgs.length; i++) this.burstImgs[i].img.setVisible(false);
    this.lastBurstId = bursts.reduce((m, b) => Math.max(m, b.id), this.lastBurstId);
    void dt;
  }

  private updateParticles(dt: number): void {
    for (const p of this.particles) {
      if (!p.active) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        p.img.setVisible(false);
        continue;
      }
      const d = Math.max(0, 1 - p.drag * dt);
      p.vx *= d;
      p.vy = p.vy * d + p.grav * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      const k = p.life / p.ttl;
      p.img.setPosition(p.x, p.y);
      p.img.setRotation(p.streak ? Math.atan2(p.vy, p.vx) : p.rot);
      const w = p.size * (p.streak ? 3.4 : 1) * (0.6 + (1 - k) * 0.7);
      p.img.setDisplaySize(w, p.size * (p.streak ? 0.7 : 1) * (0.6 + (1 - k) * 0.7));
      p.img.setTint(p.color);
      p.img.setAlpha(Math.pow(k, 0.7));
      p.img.setDepth(3900 + p.y);
    }
  }

  // ---------------------------------------------------------------- camera

  private addFlash(color: number, alpha: number, scale: number): void {
    this.flashAlpha = Math.max(this.flashAlpha, alpha * scale);
    this.flashColor = color;
  }

  private updateFlash(dt: number): void {
    if (this.flashAlpha <= 0.001) {
      if (this.flashRect.visible) this.flashRect.setVisible(false);
      return;
    }
    this.flashAlpha = Math.max(0, this.flashAlpha - dt * 3.6);
    this.flashRect.setSize(this.scene.scale.width + 40, this.scene.scale.height + 40);
    this.flashRect.setFillStyle(this.flashColor, this.flashAlpha);
    this.flashRect.setDepth(8800);
    this.flashRect.setVisible(true);
  }

  private consumeShake(): void {
    if (this.pendingShake > 0.02) this.shake(this.pendingShake);
    this.pendingShake = 0;
  }

  /** Rung màn hình — càng nặng thì rung càng mạnh và càng lâu. */
  shake(power = 1): void {
    const dur = 80 + power * 90;
    this.scene.cameras.main.shake(dur, 0.003 + power * 0.0022);
  }

  /** Đẩy camera vào gần rồi trả lại — dùng cho tuyệt kỹ. */
  zoomPunch(power = 1): void {
    const cam = this.scene.cameras.main;
    if (this.scene.time.now < this.zoomUntil) return;
    this.zoomUntil = this.scene.time.now + 320;
    cam.setZoom(1 + 0.035 * power);
    this.scene.tweens.add({ targets: cam, zoom: 1, duration: 300, ease: 'Cubic.easeOut' });
  }

  /**
   * CHỮ TUYỆT KỸ giữa màn hình — "hoành tráng" không chỉ nằm ở hạt,
   * mà còn ở việc người chơi BIẾT mình vừa tung chiêu gì.
   * Chỉ gọi cho chiêu nặng (weight >= 4) để không thành nhiễu.
   */
  callout(text: string, color = 0xfde68a, power = 4): void {
    const cam = this.scene.cameras.main;
    this.calloutUntil = this.scene.time.now + 900;
    this.calloutText.setText(text);
    this.calloutText.setColor('#' + (color & 0xffffff).toString(16).padStart(6, '0'));
    this.calloutText.setPosition(cam.width / 2, cam.height * 0.28);
    this.calloutText.setScale(0.6 + power * 0.04);
    this.calloutText.setAlpha(1);
    this.calloutText.setVisible(true);
    this.scene.tweens.add({
      targets: this.calloutText,
      scaleX: this.calloutText.scaleX * 1.16,
      scaleY: this.calloutText.scaleY * 1.16,
      duration: 140,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
  }

  /** Nhấp nháy đỏ toàn màn hình khi Nhà bị đập — cảnh báo nguy hiểm. */
  dangerFlash(strength = 0.35): void {
    this.addFlash(0xff2d2d, strength, 1);
  }

  /** Cập nhật chữ tuyệt kỹ (mờ dần rồi ẩn). */
  private updateCallout(): void {
    if (!this.calloutText.visible) return;
    const left = this.calloutUntil - this.scene.time.now;
    if (left <= 0) {
      this.calloutText.setVisible(false);
      return;
    }
    this.calloutText.setAlpha(Math.min(1, left / 380));
  }

  /** Xoá sạch khi đổi màn. */
  clear(): void {
    for (const t of this.texts) t.destroy();
    for (const b of this.burstImgs) b.img.destroy();
    /*
     * HẠT: chỉ TẮT, KHÔNG huỷ và KHÔNG xoá mảng.
     * Giữ pool lại để lần vào màn sau không phải dựng lại 340 ảnh, và để `emit()`
     * luôn có hạt sẵn (xem bẫy trong `emit`).
     */
    for (const p of this.particles) {
      p.active = false;
      p.img.setVisible(false);
    }
    this.texts = [];
    this.burstImgs = [];
    this.lastBurstId = 0;
    this.flashAlpha = 0;
    this.flashRect.setVisible(false);
    this.calloutText.setVisible(false);
  }
}
